import _ from 'lodash';
import { useLayoutEffect, useMemo, useRef } from 'react';
import useAppSelector from '../../app/redux/useAppSelector';
import useIsPublicView from '../../utils/useIsPublicView';
import type {
  MagneticWallContentRect,
  MagneticWallDisplayItem,
} from '../MagneticWall';
import type { MagneticWallItemSize } from '../MagneticWallItem';
import useBannerOverlap from '../useBannerOverlap';
import type { DisplayId } from '../utils/toDisplayId';
import toDisplayId from '../utils/toDisplayId';
import styles from './usePositionsWithoutOverlaps.module.scss';

export const MIN_COMMENT_HEIGHT = parseInt(styles.minCommentHeight, 10);
export const POPOVER_BELOW_CONTENT_OFFSET = 8;
const AVOID_CONTENT_THRESHOLD_HEIGHT = 380;
export const THREAD_SPACING = 12;
const BOTH_SIDES_THREAD_SPACING = 2 * THREAD_SPACING;
const TOPBAR_HEIGHT = parseInt(styles.topbarHeight, 10);
const POST_STATE_BAR_HEIGHT = parseInt(styles.postStateBarHeight, 10);
const TITLE_FONT_SIZE = parseInt(styles.fontH1Size, 10);
const MAGNETIC_WALL_BANNER_OFFSET = parseInt(
  styles.magneticWallBannerOffset,
  10,
);

export type Heights = Record<DisplayId, number | null>;
export type ContentRects = Record<string, MagneticWallContentRect>;
export type WiderLineRange = {
  top: number;
  bottom: number;
  absoluteBottom: number;
};

type IntermediatePosition = {
  displayId: DisplayId;
  contentHeight: number;
  height: number;
  top: number;
  absoluteBottom: number;
};

type ResultPosition = { top: number };

function hasItems<Item>(items: Item[]): items is [Item, ...Item[]] {
  return items.length !== 0;
}

const isOffScreenAbove = (absolutePosition: number, topThreshold: number) =>
  absolutePosition < topThreshold;

const findCollidingWiderLine = (
  widerLineRanges: WiderLineRange[],
  top: number,
  requiredSpace: number,
) =>
  widerLineRanges.findLast(
    (range) => range.top < top + requiredSpace && range.bottom > top,
  );

const findNextWiderLineWithEnoughFollowingSpace = (
  widerLineRanges: WiderLineRange[],
  top: number,
  requiredSpace: number,
) => {
  // Filter wider lines that collides or are bellow the element
  const filteredRanges = widerLineRanges.filter((range) => range.bottom > top);

  if (filteredRanges[0] && filteredRanges[0].top > top + requiredSpace) {
    // Not colliding and there is enough space above the first wider line
    return undefined;
  }
  return filteredRanges.find((range, i) => {
    const next = filteredRanges[i + 1];
    // Either last wider range or element fits between this and the next one
    return !next || range.bottom + requiredSpace < next.top;
  });
};

const findPreviousWiderLineWithEnoughPrecedingSpace = (
  widerLineRanges: WiderLineRange[],
  top: number,
  requiredSpace: number,
) => {
  const bottom = top + requiredSpace;
  // Filter wider lines that are above the element
  const filteredRanges = widerLineRanges
    .filter((range) => range.top < bottom)
    .toReversed();

  if (filteredRanges[0] && filteredRanges[0].bottom < top) {
    // There is enough space bellow the first wider line
    return undefined;
  }
  return filteredRanges.find((range, i) => {
    const prev = filteredRanges[i + 1];
    // Either first wider range or element between this and the previous one
    return !prev || range.top > prev.bottom + requiredSpace;
  });
};

const getPositionUnderCollidingWiderLine = ({
  current,
  collidingWiderLine,
  widerLineRanges,
  top,
  boundaries,
  compact,
}: {
  current: IntermediatePosition;
  collidingWiderLine: WiderLineRange;
  widerLineRanges: WiderLineRange[];
  top: number;
  boundaries: { top?: number | null; bottom?: number | null };
  compact: boolean;
}) => {
  const currentHeightWithSpacing = current.height + BOTH_SIDES_THREAD_SPACING;

  const topAboveCollidingWiderLine =
    collidingWiderLine.top - current.height + (compact ? 0 : THREAD_SPACING);

  const previousWiderLine = widerLineRanges.findLast(
    (range) => range.top < collidingWiderLine.top,
  );

  const topBoundary = Math.max(
    boundaries.top ?? -Infinity,
    previousWiderLine?.bottom ?? -Infinity,
  );

  if (
    current.top < collidingWiderLine.top &&
    topAboveCollidingWiderLine >= topBoundary
  ) {
    // Can be safely placed above the colliding wider line
    return topAboveCollidingWiderLine;
  }
  const lastWiderLineWithEnoughFollowingSpace =
    findNextWiderLineWithEnoughFollowingSpace(
      widerLineRanges,
      top,
      currentHeightWithSpacing,
    );

  if (lastWiderLineWithEnoughFollowingSpace) {
    const topBellowWiderLine = lastWiderLineWithEnoughFollowingSpace
      ? lastWiderLineWithEnoughFollowingSpace.bottom + BOTH_SIDES_THREAD_SPACING
      : 0;
    if (
      !boundaries.bottom ||
      topBellowWiderLine + current.height < boundaries.bottom
    ) {
      return (
        lastWiderLineWithEnoughFollowingSpace.bottom + BOTH_SIDES_THREAD_SPACING
      );
    }
  }
  return null;
};

const getAvoidContentOverlapOffset = (contentHeight: number) => {
  return contentHeight < AVOID_CONTENT_THRESHOLD_HEIGHT
    ? contentHeight + POPOVER_BELOW_CONTENT_OFFSET
    : 0;
};

function adjustPositions({
  intermediatePositions,
  widerLineRanges,
  priorityIndex,
  positions,
  topThreshold,
  compact,
  bannerOverlap,
}: {
  intermediatePositions: [IntermediatePosition, ...IntermediatePosition[]];
  widerLineRanges: WiderLineRange[];
  priorityIndex: number | null;
  positions: Record<string, ResultPosition>;
  topThreshold: number;
  compact: boolean;
  bannerOverlap: number | null;
}): Record<string, ResultPosition> {
  const adjustedPositions = { ...positions };

  const firstVisibleIndex = intermediatePositions.findIndex(
    (p) => !isOffScreenAbove(p.absoluteBottom, topThreshold),
  );

  const adjustedPriorityIndex =
    priorityIndex ??
    (firstVisibleIndex >= 0
      ? firstVisibleIndex
      : intermediatePositions.length - 1);

  for (let i = adjustedPriorityIndex; i >= 0; i -= 1) {
    const current = intermediatePositions[i];
    const next = intermediatePositions[i + 1];
    const nextAdjustedPosition = next
      ? adjustedPositions[next.displayId]
      : null;

    if (i !== priorityIndex && current) {
      const minimumTopBasedOnNext = nextAdjustedPosition
        ? nextAdjustedPosition.top - current.height - THREAD_SPACING
        : current.top;

      let top = Math.min(current.top, minimumTopBasedOnNext);

      const collidingWiderLine = findCollidingWiderLine(
        widerLineRanges,
        top,
        current.height,
      );

      if (collidingWiderLine) {
        const positionUnderCollidingWiderLine =
          getPositionUnderCollidingWiderLine({
            current,
            collidingWiderLine,
            widerLineRanges,
            top,
            boundaries: {
              top: bannerOverlap,
              bottom: nextAdjustedPosition?.top,
            },
            compact,
          });

        if (positionUnderCollidingWiderLine !== null) {
          top = positionUnderCollidingWiderLine;
        } else {
          // Trying to place element above the closest possible wider line
          const lastWiderLineWithEnoughPrecedingSpace =
            findPreviousWiderLineWithEnoughPrecedingSpace(
              widerLineRanges,
              top,
              current.height + THREAD_SPACING,
            );

          if (lastWiderLineWithEnoughPrecedingSpace) {
            top = Math.min(
              top,
              lastWiderLineWithEnoughPrecedingSpace.top -
                current.height -
                THREAD_SPACING,
            );
          }
        }
      }

      adjustedPositions[current.displayId] = { top };
    }
  }

  for (
    let i = adjustedPriorityIndex + 1;
    i < intermediatePositions.length;
    i += 1
  ) {
    const current = intermediatePositions[i];
    const prev = intermediatePositions[i - 1];

    const adjustedPrevPosition = prev
      ? adjustedPositions[prev.displayId]
      : null;

    if (
      i !== priorityIndex &&
      current &&
      !isOffScreenAbove(current.absoluteBottom, topThreshold) &&
      (i === 0 || adjustedPrevPosition)
    ) {
      const minimumTopBasedOnPrev =
        adjustedPrevPosition && prev
          ? adjustedPrevPosition.top + prev.height + THREAD_SPACING
          : undefined;

      let top = Math.max(current.top, minimumTopBasedOnPrev ?? 0);

      const collidingWiderLine = findCollidingWiderLine(
        widerLineRanges,
        top,
        current.height,
      );

      if (collidingWiderLine) {
        const positionUnderCollidingWiderLine =
          getPositionUnderCollidingWiderLine({
            current,
            collidingWiderLine,
            widerLineRanges,
            top,
            boundaries: { top: minimumTopBasedOnPrev },
            compact,
          });

        if (positionUnderCollidingWiderLine !== null) {
          top = positionUnderCollidingWiderLine;
        }
      }

      adjustedPositions[current.displayId] = { top };
    }
  }

  return adjustedPositions;
}

function extendWithContentRects(
  items: MagneticWallDisplayItem[],
  contentRects: ContentRects,
) {
  return _.compact(
    items.map((item) => {
      const contentRect = contentRects[item.data.id];
      if (!contentRect) return null;

      return {
        ...item,
        contentRect,
      };
    }),
  );
}

function sortByContentRectPosition(
  a: { contentRect: MagneticWallContentRect },
  b: { contentRect: MagneticWallContentRect },
) {
  return a.contentRect.top === b.contentRect.top
    ? a.contentRect.left - b.contentRect.left
    : a.contentRect.top - b.contentRect.top;
}

function toIntermediatePosition({
  item,
  heights,
  bannerOverlap,
}: {
  item: MagneticWallDisplayItem & { contentRect: MagneticWallContentRect };
  heights: Heights;
  bannerOverlap: number | null;
}): IntermediatePosition {
  const height = heights[item.displayId] ?? MIN_COMMENT_HEIGHT;
  const { bottom, absoluteBottom } = item.contentRect;
  const top =
    typeof bannerOverlap === 'number'
      ? item.contentRect.top + bannerOverlap
      : item.contentRect.top;

  return {
    displayId: item.displayId,
    contentHeight: bottom - top,
    height,
    top,
    absoluteBottom,
  };
}

export function getPositionsWithoutOverlaps({
  heights,
  magneticWallItems,
  shouldAvoidContentOverlap,
  compact,
  widerLineRanges,
  contentRects,
  priorityId,
  topThreshold,
  bannerOverlap,
}: {
  heights: Heights;
  magneticWallItems: MagneticWallDisplayItem[];
  shouldAvoidContentOverlap: boolean;
  compact: boolean;
  widerLineRanges: WiderLineRange[];
  contentRects: ContentRects;
  priorityId: DisplayId | null;
  topThreshold: number;
  bannerOverlap: number | null;
}) {
  const intermediatePositions = extendWithContentRects(
    magneticWallItems,
    contentRects,
  )
    .toSorted(sortByContentRectPosition)
    .map((item, index) =>
      toIntermediatePosition({
        item,
        heights,
        bannerOverlap:
          bannerOverlap !== null &&
          index === 0 &&
          item.contentRect.top <= TITLE_FONT_SIZE
            ? bannerOverlap
            : null,
      }),
    );

  if (!hasItems(intermediatePositions)) {
    return {};
  }

  const foundPriorityIndex = priorityId
    ? intermediatePositions.findIndex((item) => item.displayId === priorityId)
    : -1;
  const priorityIndex = foundPriorityIndex !== -1 ? foundPriorityIndex : null;

  const priorityItem =
    intermediatePositions[priorityIndex ?? 0] ?? intermediatePositions[0];

  const top = shouldAvoidContentOverlap
    ? priorityItem.top +
      getAvoidContentOverlapOffset(priorityItem.contentHeight)
    : priorityItem.top;

  const positions = {
    [priorityItem.displayId]: { top },
  };

  return adjustPositions({
    intermediatePositions,
    widerLineRanges,
    priorityIndex,
    positions,
    topThreshold,
    compact,
    bannerOverlap,
  });
}

function usePositionsWithoutOverlaps({
  heights,
  magneticWallItems,
  widerLineRanges,
  contentRects,
  magneticWallItemSize,
  isBannerIntersecting,
}: {
  heights: Heights;
  magneticWallItems: MagneticWallDisplayItem[];
  widerLineRanges: WiderLineRange[];
  contentRects: ContentRects;
  magneticWallItemSize: MagneticWallItemSize;
  isBannerIntersecting: boolean;
}) {
  const prevPositionsRef = useRef<
    ReturnType<typeof getPositionsWithoutOverlaps>
  >({});
  const magneticWallFocus = useAppSelector(
    (state) => state.editor.magneticWallFocus,
  );
  const bannerOverlap = useBannerOverlap();
  const isPublic = useIsPublicView();

  const hasStateBar = useAppSelector((state) => state.editor.hasStateBar);

  const shouldAvoidContentOverlap =
    magneticWallItemSize === 'compact' && !!magneticWallFocus?.active;

  const priorityId = magneticWallFocus ? toDisplayId(magneticWallFocus) : null;

  const positions = useMemo(() => {
    const magneticWallBannerOffset = isPublic ? 0 : MAGNETIC_WALL_BANNER_OFFSET;

    return getPositionsWithoutOverlaps({
      heights,
      magneticWallItems,
      shouldAvoidContentOverlap,
      compact: magneticWallItemSize === 'compact',
      widerLineRanges,
      contentRects,
      priorityId,
      topThreshold: TOPBAR_HEIGHT + (hasStateBar ? POST_STATE_BAR_HEIGHT : 0),
      bannerOverlap: isBannerIntersecting
        ? bannerOverlap - magneticWallBannerOffset + THREAD_SPACING
        : null,
    });
  }, [
    heights,
    magneticWallItems,
    shouldAvoidContentOverlap,
    magneticWallItemSize,
    widerLineRanges,
    contentRects,
    priorityId,
    isBannerIntersecting,
    hasStateBar,
    bannerOverlap,
    isPublic,
  ]);

  useLayoutEffect(() => {
    prevPositionsRef.current = positions;
  }, [positions]);

  return positions;
}

export default usePositionsWithoutOverlaps;
